/**
 * QuickCreateMenu — Menu compacto do botão "+" ao lado da busca global
 *
 * Lista curta (estilo Pipedrive) com as ações marcadas como `primary` no
 * registro `QUICK_CREATE_ACTIONS`: Contato, Cliente, Negócio, Tarefa,
 * Evento e Iniciar conversa. Cada linha mostra ícone, rótulo e a tecla de
 * atalho correspondente.
 *
 * Segurança/permissões: a lista já vem filtrada por `useAvailableQuickCreates`
 * (flags de módulo do tenant + permissões de menu do usuário).
 *
 * Atalhos: com o menu aberto, a tecla simples (C, L, N, T, E, I) aciona a
 * ação. Fora do menu, o hook global `useQuickCreateShortcuts` (montado pelo
 * HeaderSearchSlot) responde por `Alt + tecla` em qualquer tela — por isso a
 * dica exibida na lista é `Alt+<tecla>`.
 *
 * RELEASE: 2026-08-06 v1.1.0 - Dica de atalho global (Alt+tecla) e suporte a
 *          Alt dentro do menu.
 * RELEASE: 2026-08-06 v1.0.0 - Criação.

 */
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useAvailableQuickCreates } from '@/hooks/useAvailableQuickCreates';
// Reutiliza a detecção de campo editável do hook de atalhos globais.
import { isEditableTarget } from '@/hooks/useQuickCreateShortcuts';
import type { QuickCreateDef } from '@/config/quickCreate';
import {
  useQuickCreateModals,
  isQuickCreateModalKey,
} from '@/contexts/QuickCreateModalsContext';


import { cn } from '@/lib/utils';

interface QuickCreateMenuProps {
  /** Fecha o popover pai após acionar uma ação. */
  onClose: () => void;
  /** Abre o popover completo de ações rápidas (rodapé "Ver todas as ações"). */
  onOpenAll?: () => void;
}

export function QuickCreateMenu({ onClose, onOpenAll }: QuickCreateMenuProps) {
  const navigate = useNavigate();
  const available = useAvailableQuickCreates();
  // v1.2.0: modais "no lugar" (null quando o provider não está montado).
  const quickModals = useQuickCreateModals();

  // Somente as ações "principais", preservando a ordem do registro.
  const primaryActions = useMemo(
    () => available.filter((a) => a.primary),
    [available],
  );

  /**
   * Aciona a ação:
   *  - `inline`: abre o modal sobre a tela atual (sem navegar);
   *  - demais: navega para a rota com `?new=` (a página destino abre o modal).
   */
  const run = (action: QuickCreateDef) => {
    if (action.inline && quickModals && isQuickCreateModalKey(action.key)) {
      quickModals.openQuickCreate(action.key);
      onClose();
      return;
    }
    const separator = action.route.includes('?') ? '&' : '?';
    navigate(`${action.route}${separator}new=${action.newParam}`);
    onClose();
  };


  // Atalhos locais: tecla simples (ou Alt+tecla) enquanto o menu está aberto.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) return;
      // Ignora digitação em campos editáveis (exceto com Alt).
      if (!e.altKey && isEditableTarget(e.target)) return;
      const key = e.key.toUpperCase();
      const match = primaryActions.find((a) => a.shortcutKey === key);
      if (match) {
        e.preventDefault();
        e.stopPropagation();
        run(match);
      }
    };
    // Captura para responder antes do listener global (evita disparo duplo).
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryActions]);


  if (primaryActions.length === 0) return null;

  return (
    <Command className="rounded-2xl">
      <CommandList className="max-h-[70vh]">
        <CommandGroup className="p-2">
          {primaryActions.map((action) => {
            const Icon = action.icon;
            return (
              <CommandItem
                key={action.key}
                value={action.key}
                onSelect={() => run(action)}
                className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5"
              >
                <Icon className={cn('h-[18px] w-[18px]', action.accent ?? 'text-muted-foreground')} />
                <span className="flex-1 text-sm font-medium">
                  {action.shortLabel ?? action.label}
                </span>

                {action.shortcutKey && (
                  <kbd className="rounded-md border bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                    Alt+{action.shortcutKey}
                  </kbd>
                )}

              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>

      {onOpenAll && (
        <button
          type="button"
          onClick={onOpenAll}
          className="w-full border-t px-4 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
        >
          Ver todas as ações
        </button>
      )}
    </Command>
  );
}
